import QRCode from 'easyqrcodejs'
import { DateUtil } from './DateUtil'
import html2canvas from 'html2canvas'

// 客户端通过canvas生成poster的实现
export class CanvasPosterUtil {
  static getBase64Image(img: HTMLImageElement): string | null {
    if (!img) {
      return null
    }

    const scale = window.devicePixelRatio

    const width = img.width * scale
    const height = img.height * scale

    const canvas = document.createElement('canvas')

    canvas.width = width
    canvas.height = height
    // console.log('==================')
    // console.log('scaleDraw2', scale, width, height)

    const ctx = canvas.getContext('2d')

    if (ctx) {
      ctx.drawImage(img, 0, 0, width, height)
      return canvas.toDataURL('image/png')
    }

    return null
  }

  static async domToImage(el: HTMLElement, quality = 0.5): Promise<string> {
    return html2canvas(el, {
      scrollY: 0,
      useCORS: true,
      scale: 1125 / Math.max(el.getBoundingClientRect().width, 375),
      width: Math.floor(el.getBoundingClientRect().width),
      height: Math.floor(el.getBoundingClientRect().height),
    }).then((canvas: HTMLCanvasElement) => {
      return canvas.toDataURL('image/jpeg', quality)
    })
  }
}

export interface Position {
  x: number
  y: number
}

export class CanvasPoster {
  // 构建canvas， width, height为背景图片的原始尺寸，建议750宽度
  // 生成的图片原始大小则为750， 但还是可以当成src传给img， img本身可以设置宽高适应界面
  public static async getOrCreateCanvasElement(width: number, height: number): Promise<HTMLCanvasElement> {
    const canvasId = 'canvas_poster_wrapper'
    let ele = document.getElementById(canvasId)
    if (!ele) {
      ele = document.createElement('canvas')
      ele.id = canvasId
      document.body.appendChild(ele)
    }
    ele.style.display = 'none'
    const aele = ele as any
    aele.width = width
    aele.height = height
    // canvas 似乎要一些事件初始化
    await DateUtil.sleep(200)
    return ele as HTMLCanvasElement
  }

  protected canvas: HTMLCanvasElement
  protected context: CanvasRenderingContext2D

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas
    const context = canvas.getContext('2d')
    if (context) {
      const c = context as any
      c.webkitImageSmoothingEnabled = false
      c.mozImageSmoothingEnabled = false
      context.imageSmoothingEnabled = false
      this.context = context
    } else {
      throw new Error('get context fail')
    }
  }

  /**
   * @param resources eg
   * [
        {
          type: 'image',
          image: bg,
          position: { x: 0, y: 0 },
          size: { width: 750, height: 1334 },
          sort: 1
        },
        {
          type: 'text',
          text: this.userInfo.wechat_name || '',
          position: { x: 180, y: 50 },
          styles: {
            font: '35px bold',
            fillStyle: '#000',
            textBaseline: 'top'
          }
        },
        {
          type: 'qrcode',
          url: inviteURL,
          position: { x: 80, y: 1015 },
          width: 500,
          size: { width: 250, height: 250 }
        },
        {
          type: 'roundImage',
          image:  require('./assets/default_avatar.png')
          center: { x: 100, y: 80 },
          radius: 60
        }
      ]
   */
  async getPoster(resources: any[]) {
    // 由于真实的绘制是由图片加载时长确定的，为了保证绘制的顺序
    // 将加载好后的图片放进队列，按优先级取出并绘制
    // 顺序有 index 属性确定，如果都没有顺序则按代码的顺序确定
    // 文字始终最后绘制
    const imageLoadedQueue = []
    const allImages = []

    // 按 index 排序从大到小排序
    resources.sort((a: any, b: any) => {
      if (!a.sort) a.sort = 0
      if (!b.sort) b.sort = 0
      return b.sort - a.sort
    })

    // 获取图片元素
    for (let i = 0; i < resources.length; i++) {
      const resource = resources[i]
      if (resource.type === 'image' || resource.type === 'roundImage') {
        imageLoadedQueue.push(this.loadImage(resource.image))
        allImages.push(resource)
      }
      if (resource.type === 'qrcode') {
        imageLoadedQueue.push(this.createQrcode(resource.url, resource.width))
        allImages.push(resource)
      }
    }

    // 图片下载完成后的数组
    const couldDrawImages = await Promise.all(imageLoadedQueue)

    // 绘制图片
    allImages.forEach((resource, index) => {
      if (resource.type === 'roundImage') {
        this.drawRoundImage(couldDrawImages[index] as HTMLImageElement, resource.center, resource.radius)
      } else {
        this.drawImage(couldDrawImages[index] as HTMLImageElement, resource.position, resource.size)
      }
    })

    // 绘制文字
    resources.forEach((resource: any) => {
      if (resource.type === 'text') {
        this.drawText(resource.text, resource.position, resource.styles)
      }
    })
    return await this.getDataURL()
  }

  async getDataURL(): Promise<string> {
    return new Promise((resolve) => {
      window.requestAnimationFrame(() => {
        resolve(this.canvas.toDataURL('image/png'))
      })
    })
  }

  /**
   * canvas上绘制图片
   */
  drawImage(image: HTMLImageElement, position = { x: 0, y: 0 }, size = { width: 0, height: 0 }) {
    this.context.drawImage(image, position.x, position.y, size.width, size.height)
  }

  // canvas绘制圆形图片
  drawRoundImage(image: HTMLImageElement, center: Position, radius: number) {
    this.context.save()
    this.context.arc(center.x, center.y, radius, 0, 2 * Math.PI, false)
    this.context.clip()
    this.drawImage(
      image,
      {
        x: center.x - radius,
        y: center.y - radius,
      },
      {
        width: 2 * radius,
        height: 2 * radius,
      }
    )
    this.context.restore()
  }

  /**
   * 绘制文字
   * @param {String} text 绘制的文字
   * @param {position} position 坐标
   * @param {Object} styles 样式
   */
  drawText(text: string, position: Position, styles: any) {
    this.context.save()
    for (const propName in styles) {
      const c = this.context as any
      c[propName] = styles[propName]
    }
    this.context.fillText(text, position.x, position.y)
    this.context.restore()
  }

  async drawQrcode(url: string, width: number, position: Position, size: { width: number; height: number }) {
    const qrcode = await this.createQrcode(url, width)
    this.drawImage(qrcode as HTMLImageElement, position, size)
  }

  /**
   * 生成二维码
   * @param {*} url 二维码内容
   * @param {*} options 生成的二维码参数
   */
  async createQrcode(url: string, width: number): Promise<any> {
    const dataURL = await CanvasPoster.createQRCodeImageURL(url, width)
    return this.loadImage(dataURL, false)
  }

  static async createQRCodeImageURL(url: string, width = 500, logo?: string, logoWidth = 16): Promise<string> {
    return new Promise((resolve) => {
      const container = document.createElement('div')

      new QRCode(container, {
        text: url,
        width: width,
        height: width,
        correctLevel: QRCode.CorrectLevel.M,
        logo,
        logoWidth: logoWidth,
        logoHeight: logoWidth,
        crossOrigin: 'anonymous',
        onRenderingEnd: (_options: any, _dataUrl: string) => {
          resolve(_dataUrl)
        },
      })
    })
  }

  // 加载图片
  loadImage(src: string, cross = true) {
    return new Promise((resolve, reject) => {
      const image = new Image()
      cross && (image.crossOrigin = 'Anonymous')
      image.onload = () => {
        resolve(image)
      }
      image.onerror = (e) => {
        reject(e)
      }
      image.src = src
    })
  }
}
